// src/services/cxpDeudaService.js
import {
  addDoc, collection, doc, getDoc, runTransaction, updateDoc,
  Timestamp, arrayUnion, serverTimestamp
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { getAuth } from "firebase/auth";
import { metodoPagoPayload } from "../helpers/metodosPago";
import { toJsDate, toLocalISODate, hoyLocalISO } from "../helpers/fechas";
import { ensureProveedorPorNombre } from "./proveedoresService";
import { registrarAuditoria } from "../helpers/auditoria";
import { assertCorteAbierto } from "./cajaProyectoService";

// ===== Util UI =====
export const money = (v) =>
  Number(v ?? 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  });

// ===== Util: id corto estable para cada pago =====
const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// ===== Helpers de fechas =====
function atMidnight(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
function clampDay(year, monthIndex, day) {
  // último día del mes (monthIndex: 0-11)
  const last = new Date(year, monthIndex + 1, 0).getDate();
  return Math.min(Math.max(1, day), last);
}
function firstMonthlyDueFromToday(anchorDay) {
  // primer vencimiento anclado al día de mes (hoy o el siguiente mes)
  const today = atMidnight(new Date());
  const y = today.getFullYear();
  const m = today.getMonth();
  const targetThisMonth = new Date(y, m, clampDay(y, m, anchorDay));
  if (targetThisMonth > today) return targetThisMonth;
  // si ya pasó, siguiente mes
  const nextMonth = m + 1;
  const y2 = y + Math.floor(nextMonth / 12);
  const m2 = nextMonth % 12;
  return new Date(y2, m2, clampDay(y2, m2, anchorDay));
}
function addDays(baseDate, days) {
  const d = atMidnight(baseDate);
  d.setDate(d.getDate() + days);
  return d;
}
function addOneMonthAnchored(baseDate, anchorDay) {
  // Avanza exactamente 1 mes desde baseDate manteniendo el día ancla (ajustado)
  const base = atMidnight(baseDate);
  const nextMonth = base.getMonth() + 1;
  const y = base.getFullYear() + Math.floor(nextMonth / 12);
  const m = nextMonth % 12;
  const day = clampDay(y, m, anchorDay ?? base.getDate());
  return new Date(y, m, day);
}

function parseDateInput(value) {
  if (!value) return null;
  const d = atMidnight(new Date(`${value}T00:00:00`));
  return Number.isNaN(d.getTime()) ? null : d;
}

// ===== Crear CxP =====
export async function createCxpDeuda({
  proveedorId = "",
  proveedorNombre = "",
  referencia = "",
  importe = 0,
  enganche = 0,
  mensualidad = 0,
  frecuencia = "mensual", // 'mensual' | 'quincenal' | 'semanal'
  diaPago = 1,            // usado solo para mensual
  notas = "",
  proyectoId = "",
  proyectoNombre = "",
  fraccionamientoId = "",
  fraccionamientoNombre = "",
  obraId = "",
  obraNombre = "",
  propiedadId = "",
  propiedadFolio = "",
  tipoCosto = "administrativo",
  metodoProrrateo = "ninguno",
  capitalizable = false,
  fechaVencimiento = "",
  contexto = null,          // objeto libre — usado por comisiones para trazabilidad
}) {
  const nowTs = Timestamp.now();
  const proveedorSeguro = await ensureProveedorPorNombre({ proveedorId, proveedorNombre });

  // Normalizaciones numéricas
  const imp = Math.max(Number(importe || 0), 0);
  const eng = Math.max(Number(enganche || 0), 0);
  const men = Math.max(Number(mensualidad || 0), 0);
  const saldoRestante = Math.max(imp - eng, 0);

  // nextDueDate inicial
  let nextDate;
  const explicitDueDate = parseDateInput(fechaVencimiento);
  if (explicitDueDate) {
    nextDate = explicitDueDate;
  } else if (frecuencia === "mensual") {
    const anchor = Math.min(Math.max(Number(diaPago || 1), 1), 31);
    nextDate = firstMonthlyDueFromToday(anchor);
  } else if (frecuencia === "quincenal") {
    nextDate = addDays(new Date(), 14);
  } else {
    nextDate = addDays(new Date(), 7); // semanal
  }

  const payload = {
    proveedorId: proveedorSeguro.id,
    proveedorNombre: proveedorSeguro.nombre || proveedorNombre,
    referencia,
    importe: imp,
    enganche: eng,
    mensualidad: men,
    frecuencia,
    diaPago: frecuencia === "mensual" ? Number(diaPago || 1) : null,
    notas,
    proyectoId,
    proyectoNombre,
    fraccionamientoId,
    fraccionamientoNombre,
    obraId,
    obraNombre,
    propiedadId,
    propiedadFolio,
    tipoCosto,
    metodoProrrateo,
    capitalizable: !!capitalizable,
    saldoRestante,
    fechaVencimiento: explicitDueDate ? Timestamp.fromDate(explicitDueDate) : null,
    nextDueDate: saldoRestante > 0 ? Timestamp.fromDate(nextDate) : null,
    creada: nowTs,
    lastPaymentDate: null,
    pagos: [],
    activo: saldoRestante > 0,
    ...(contexto ? { contexto } : {}),
  };

  const ref = await addDoc(collection(db, "cxp_deudas"), payload);
  return ref;
}

// ===== Cargar CxP =====
export async function loadCxpDeuda(deudaId) {
  const snap = await getDoc(doc(db, "cxp_deudas", deudaId));
  if (!snap.exists()) throw new Error("CxP no encontrada");
  return { id: snap.id, ...snap.data() };
}

// ===== Procesar pago =====
// Nueva firma con 'extras' para metadatos del pago.
export async function processCxpPayment(
  deudaId,
  monto,
  metodo = "efectivo",
  extras = {} // {cobradorId?, cobradorNombre?, referencia?, notas?}
) {
  const nowTs = Timestamp.now();
  const deudaRef = doc(db, "cxp_deudas", deudaId);
  const metodoInfo = metodoPagoPayload(metodo);

  const auth = getAuth();
  const user = auth.currentUser;

  // Prepara metadatos del pago
  const cobradorId = extras?.cobradorId ?? user?.uid ?? null;
  const cobradorNombre = extras?.cobradorNombre ?? user?.displayName ?? user?.email ?? "—";
  const referencia = extras?.referencia ?? "";
  const notas = extras?.notas ?? "";

  // AUDIT A-8 (decision de negocio 2026-06-17): BLOQUEO DURO. Todo pago de CxP
  // debe salir de una caja — no existe caso legitimo de pagar a un proveedor
  // sin que el dinero salga de alguna caja del proyecto. Antes solo se auditaba
  // y se permitia, lo que dejaba huecos de conciliacion permanentes.
  // Escape hatch `extras.permitirSinCaja=true` SOLO para fixtures/migraciones
  // (scripts), nunca desde la UI.
  if (!extras?.cajaMovimiento && !extras?.permitirSinCaja) {
    throw new Error(
      "Pago de CxP bloqueado: requiere un movimiento de caja asociado. " +
      "Todo pago a proveedor debe salir de una caja."
    );
  }
  if (!extras?.cajaMovimiento && extras?.permitirSinCaja) {
    // Camino explicito de fixture/migracion: auditar el hueco para trazabilidad.
    console.warn("[CAJA-002] processCxpPayment con permitirSinCaja — pago sin movimiento de caja (fixture/migracion).", { deudaId, monto });
    try {
      await registrarAuditoria({
        accion: "cxp.pagoSinCaja",
        entidadTipo: "cxp_deudas",
        entidadId: deudaId,
        detalle: "Pago de CxP registrado sin cajaMovimiento (permitirSinCaja=true, fixture/migracion)",
        despues: { monto, metodo, extras },
      });
    } catch (_) { /* no bloquear el pago si la auditoría falla */ }
  }

  // CR-1: validar corte de caja ABIERTO antes de la tx. assertCorteAbierto hace
  // getDocs (no puede correr dentro de runTransaction). El pago de CxP crea el
  // movimiento desde extras.cajaMovimiento, así que validamos ese scope/fecha.
  if (extras?.cajaMovimiento) {
    const cm = extras.cajaMovimiento;
    const cmFecha = parseDateInput(cm.fecha) || new Date();
    try {
      await assertCorteAbierto({
        cajaScope: cm.cajaScope || "proyecto",
        cajaId: cm.cajaId || cm.proyectoId || "",
        fechaMs: cmFecha.getTime(),
      });
    } catch (e) {
      if (String(e?.message || "").startsWith("Período cerrado")) throw e;
      console.warn("[CR-1] no se pudo validar corte de caja (CxP):", e);
    }
  }

  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(deudaRef);
    if (!snap.exists()) throw new Error("CxP no encontrada");

    const data = snap.data();
    const montoAbs = Math.abs(Number(monto) || 0);
    if (montoAbs <= 0) throw new Error("Monto inválido");

    if (data.cancelada === true || data.estado === "cancelada") {
      throw new Error("La CxP esta cancelada y no puede recibir pagos.");
    }

    const currentSaldo = Number(data.saldoRestante || 0);
    if (currentSaldo <= 0) {
      throw new Error("La CxP no tiene saldo pendiente.");
    }
    if (montoAbs > currentSaldo) {
      throw new Error("El pago excede el saldo pendiente.");
    }

    let cajaRef = null;
    let cajaMovimientoId = "";
    if (extras?.cajaMovimiento) {
      cajaRef = doc(collection(db, "cajas_movimientos"));
      cajaMovimientoId = cajaRef.id;
    }

    // Pago a registrar (con metadatos y pagoId)
    const pago = {
      pagoId: rid(),
      cantidad: montoAbs,
      ...metodoInfo,
      fecha: nowTs,
      createdAt: nowTs,
      cobradorId,
      cobradorNombre,
      referencia,
      notas,
      cajaMovimientoId
    };

    // Nuevo saldo
    const nuevoSaldo = Math.max(currentSaldo - montoAbs, 0);

    // ===== Regla: mover nextDueDate UNA sola frecuencia por pago =====
    let nextDueDateTS = data.nextDueDate || null;
    if (nuevoSaldo > 0) {
      const baseDate = toJsDate(data.nextDueDate) || new Date();
      const freq = (data.frecuencia || "mensual").toLowerCase();
      if (freq === "mensual") {
        const anchor = Number(data.diaPago || baseDate.getDate());
        const next = addOneMonthAnchored(baseDate, anchor);
        nextDueDateTS = Timestamp.fromDate(next);
      } else if (freq === "quincenal") {
        nextDueDateTS = Timestamp.fromDate(addDays(baseDate, 14));
      } else {
        nextDueDateTS = Timestamp.fromDate(addDays(baseDate, 7));
      }
    } else {
      // liquidada
      nextDueDateTS = null;
    }

    if (cajaRef) {
      const cajaFecha = parseDateInput(extras.cajaMovimiento.fecha) || new Date();
      tx.set(cajaRef, {
        ...extras.cajaMovimiento,
        ...metodoInfo,
        monto: montoAbs,
        fecha: toLocalISODate(cajaFecha),
        fechaMs: cajaFecha.getTime(),
        cajaScope: extras.cajaMovimiento.cajaScope || "proyecto",
        cajaId: extras.cajaMovimiento.cajaId || extras.cajaMovimiento.proyectoId || "",
        clusterId: extras.cajaMovimiento.clusterId || "",
        transferenciaId: extras.cajaMovimiento.transferenciaId || "",
        transferenciaInterna: !!extras.cajaMovimiento.transferenciaInterna,
        origen: extras.cajaMovimiento.origen || "cxp.pago",
        cxpId: deudaId,
        cxpPagoId: pago.pagoId,
        activo: true,
        cancelado: false,
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        createdByUid: cobradorId || "",
        createdByEmail: user?.email || "",
      });
    }

    tx.update(deudaRef, {
      pagos: arrayUnion(pago),
      lastPaymentDate: nowTs,
      saldoRestante: nuevoSaldo,
      nextDueDate: nextDueDateTS,
      ...(nuevoSaldo > 0 ? {} : { activo: false, estado: "liquidada" })
    });

    return { pago, saldoAnterior: currentSaldo, saldoRestante: nuevoSaldo, cajaMovimientoId };
  });
}

// ===== Actualizar nextDueDate manualmente (desde el detalle) =====
export async function updateCxpNextDue(deudaId, yyyyMmDdStringOrEmpty) {
  const deudaRef = doc(db, "cxp_deudas", deudaId);
  let patch = {};
  if (yyyyMmDdStringOrEmpty) {
    const d = atMidnight(new Date(`${yyyyMmDdStringOrEmpty}T00:00:00`));
    patch = { nextDueDate: Timestamp.fromDate(d) };
  } else {
    patch = { nextDueDate: null };
  }
  await updateDoc(deudaRef, patch);
  return patch; // para setState en el detalle
}

// ===== Activar/Desactivar =====
// Audit A-7: si reactivamos una CxP que fue cancelada (saldoRestante=0,
// mensualidad=0, nextDueDate=null por el fix C-1), tenemos que regenerar
// esos campos a partir del importe/cantidadPagada del doc — si no,
// la CxP queda "activa" pero sin saldo ni siguiente pago en agenda.
export async function toggleCxpActivo(deudaId, nuevoActivo) {
  const ref = doc(db, "cxp_deudas", deudaId);
  if (!nuevoActivo) {
    await updateDoc(ref, { activo: false, updatedAt: Date.now() });
    return;
  }

  // Reactivar: leer doc y recalcular saldoRestante + nextDueDate si vienen
  // en cero (caso post-cancelacion).
  const snap = await getDoc(ref);
  const d = snap.exists() ? snap.data() : {};
  const importe = Number(d.importe || 0);
  const cantidadPagada = Number(d.cantidadPagada || 0);
  const saldoCalculado = Math.max(importe - cantidadPagada, 0);
  const necesitaRegenerar =
    Number(d.saldoRestante || 0) === 0 && saldoCalculado > 0;

  const patch = {
    activo: true,
    cancelada: false,
    estado: "activa",
    motivoCancelacion: "",
    updatedAt: Date.now(),
  };

  if (necesitaRegenerar) {
    patch.saldoRestante = saldoCalculado;
    // Si el doc tenia mensualidad guardada (importeOriginal/etc.), usarla;
    // si no, usar importe como pago unico.
    patch.mensualidad = Number(d.mensualidadOriginal || d.mensualidad || importe);
    // nextDueDate = hoy + 1 mes como default razonable; admin puede ajustar.
    const next = new Date();
    next.setHours(0, 0, 0, 0);
    next.setMonth(next.getMonth() + 1);
    patch.nextDueDate = next;
  }

  await updateDoc(ref, patch);
}

export async function cancelarCxp(deudaId, motivo = "") {
  const auth = getAuth();
  const user = auth.currentUser;

  // Limpiar campos financieros vivos al cancelar — evita el patron historico
  // donde un agregador olvida filtrar 'cancelada' y suma saldo fantasma.
  // Mismo enfoque que cancelarComisionesEnCascada (saldo:0). Si alguien
  // necesita el dato original (auditoria), queda en colecciones de auditoria.
  const patch = {
    activo: false,
    estado: "cancelada",
    cancelada: true,
    canceladaEn: serverTimestamp(),
    canceladaPor: user?.uid || "",
    canceladaPorEmail: user?.email || "",
    motivoCancelacion: motivo,
    saldoRestante: 0,
    mensualidad: 0,
    nextDueDate: null,
    updatedAt: Date.now(),
  };

  await updateDoc(doc(db, "cxp_deudas", deudaId), patch);
  return patch;
}
